import { ASTCall, stringifyExpr } from '../ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType, stringifyType } from '../expr_types';
import {
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors } from '../sql/internal';

// Function for concat expressions as a string. The syntax is:
// concat(expr, expr, expr, ...)

export const concat: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 100);
    assertKwargShape(call, [], false, warnings, undefined);
    for (const arg of call.args) {
      const actual = typecheckExpression(ctx, arg, warnings);
      if (actual.ty !== 'string' && actual.ty !== 'number') {
        warnings?.push(
          `Expected ${stringifyExpr(
            arg
          )} to be string or number, but got ${stringifyType(actual)}`
        );
      }
    }
    return { ty: 'string' };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const result = call.args.map((arg) => evalExpression(ctx, arg)).join('');
    return result;
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argsSql = call.args.map((arg) => exprToSql(ctx, arg));
    const argSqlExprs = call.args.map((arg, idx) =>
      arg.t === 'number'
        ? // Strip trailing .0 from numbers
          String.raw`regexp_replace(${argsSql[idx].expr}||'', '\.0$', '')`
        : argsSql[idx].expr
    );
    const resultExpr = argSqlExprs.join(',');

    return {
      expr: 'concat(' + resultExpr + ')',
      stage: safeMaxStage(argsSql, 0),
      unitExpr: null,
      error: mergeErrors(argsSql.map((a) => a.error)),
      trace: null,
    };
  },
};
